<template>
    <SwitchRoot v-bind="field?.controlAttrs.value" :id="field?.controlId ?? id" :disabled="disabled" :class="rootClasses">
        <SwitchThumb :class="thumbClasses" />
    </SwitchRoot>
</template>

<script setup lang="ts">
import { SwitchRoot, SwitchThumb } from "reka-ui";
import { computed } from "vue";

import { useFormField } from "./form/formFieldContext";

export type SwitchSize = "sm" | "md" | "lg";

type BaseSwitchProps = {
    /** Id for a switch used on its own. Inside a FormField the field's id wins, so its label keeps naming and toggling the switch. */
    id?: string;
    disabled?: boolean;
    size?: SwitchSize;
};

const props = withDefaults(defineProps<BaseSwitchProps>(), {
    disabled: false,
    size: "lg",
});

// Inside a FormField the switch (a button, so the field label's `for` names it) takes the field's
// id, describedby and invalid wiring. The field's id wins over the prop, so the label's `for` always
// matches; used bare, the prop is the id and none of the field wiring applies.
const field = useFormField();

const rootClasses = computed(() => {
    // w-fit keeps the switch its own size in a stretching column, such as a FormField. The invalid
    // ring is BaseInput's invalid border color, drawn as a ring so the switch keeps that size.
    const base = "flex w-fit rounded-full relative transition-colors data-disabled:cursor-not-allowed data-disabled:opacity-50 aria-invalid:ring-2 aria-invalid:ring-status-error";
    const unchecked = "data-[state=unchecked]:bg-switch-off hover:data-[state=unchecked]:bg-switch-off-hover";
    const checked = "data-[state=checked]:bg-switch-on";

    const sizeMap: Record<SwitchSize, string> = {
        sm: "min-w-7.5 h-4",
        md: "min-w-9 h-5",
        lg: "min-w-12.5 h-7.5",
    };

    return [base, unchecked, checked, sizeMap[props.size]];
});

const thumbClasses = computed(() => {
    const base = "my-auto bg-switch-thumb rounded-full transition-transform will-change-transform shadow-xl";

    const sizeMap: Record<SwitchSize, string> = {
        sm: "min-w-3 h-3 translate-x-0.5 data-[state=checked]:translate-x-[calc(100%+3px)]",
        md: "min-w-4 h-4 translate-x-0.5 data-[state=checked]:translate-x-[calc(100%+1px)]",
        lg: "min-w-6 h-6 translate-x-0.75 data-[state=checked]:translate-x-[calc(100%-2px)]",
    };

    return [base, sizeMap[props.size]];
});
</script>
